import React from 'react';
import Link from 'next/link';
import { Pill } from '@/components/ui/Pill';
import { LoadingPattern } from '@/components/ui/pattern-viewer';
import { LazySection } from '@/components/ui/LazySection';

interface CRODirectorySectionProps {
  filteredCROs: any[];
  viewMode: 'card' | 'list';
  extractFields: (cro: any) => any;
  technologyOptions: { id: string; name: string }[];
  platformOptions: { id: string; name: string }[];
  instrumentOptions: { id: string; name: string }[];
  softwareOptions: { id: string; name: string }[];
  selectedServiceTypes: string[];
  selectedTechnologies: string[];
  selectedCompliances: string[];
  selectedTherapeuticAreas: string[];
  selectedPlatforms: string[];
  selectedInstruments: string[];
  selectedSoftware: string[];
  selectedCountries: string[];
}

export default function CRODirectorySection({
  filteredCROs,
  viewMode,
  extractFields,
  technologyOptions,
  platformOptions,
  instrumentOptions,
  softwareOptions,
  selectedServiceTypes,
  selectedTechnologies,
  selectedCompliances,
  selectedTherapeuticAreas,
  selectedPlatforms,
  selectedInstruments,
  selectedSoftware,
  selectedCountries,
}: CRODirectorySectionProps) {
  return (
    <LazySection minHeight={300}>
      {filteredCROs.length === 0 ? (
        <div className="text-center py-20 text-neutral-500">No CROs found.</div>
      ) : viewMode === 'card' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredCROs.map((cro: any, idx: number) => {
            const { logoSrc, name, description, location, slug } = extractFields(cro);
            const serviceTypes = cro['Service Types'] || [];
            const techIds = cro['Technologies (new)'] || cro['Technologies'] || [];
            const compliance = cro['Regulatory Compliance'] || [];
            const CardContent = (
              <div className="flex flex-col items-center p-6 rounded-xl shadow-lg bg-white bg-card hover:shadow-xl transition-shadow h-full min-h-[260px] max-w-full">
                {/* Logo */}
                <div className="w-full flex justify-center items-center mb-4 bg-white">
                  {logoSrc ? (
                    <img src={logoSrc} alt={name} className="w-full max-w-full h-24 object-contain" style={{ maxHeight: 96 }} />
                  ) : (
                    <div className="w-full max-w-full h-24 flex items-center justify-center bg-neutral-100">
                      <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="64" height="64" rx="12" fill="#E5E7EB"/>
                        <path d="M20 44V40C20 37.7909 21.7909 36 24 36H40C42.2091 36 44 37.7909 44 40V44" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="32" cy="28" r="6" stroke="#A3A3A3" strokeWidth="2"/>
                      </svg>
                    </div>
                  )}
                </div>
                {/* Name */}
                <div className="w-full flex flex-col items-center text-center">
                  <div className="font-display font-bold text-xl mb-1 mt-1 truncate text-primary-700">{name}</div>
                  {location && <div className="font-sans italic text-secondary-600 text-sm mb-1">{location}</div>}
                  {description && <div className="font-sans text-base text-neutral-700 mb-2 break-words line-clamp-2">{description}</div>}
                </div>
                {/* Pills: Service Types, Technologies, Compliance, etc. */}
                <div className="flex flex-col gap-2 mt-2 w-full">
                  {/* Service Types */}
                  {selectedServiceTypes.length > 0 && Array.isArray(serviceTypes) && serviceTypes.filter((type) => selectedServiceTypes.includes(type)).length > 0 && (
                    <div>
                      <div className="text-xs text-neutral-500 font-semibold mb-1">Service Types:</div>
                      <div className="flex flex-row flex-wrap gap-2">
                        {Array.from(new Set(serviceTypes.filter((type) => selectedServiceTypes.includes(type)))).map((type, i) => (
                          <Pill key={"svc-"+type} type="serviceType">{type}</Pill>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Technologies */}
                  {selectedTechnologies.length > 0 && Array.isArray(techIds) && techIds.filter((tech) => selectedTechnologies.includes(tech)).length > 0 && (
                    <div>
                      <div className="text-xs text-neutral-500 font-semibold mb-1">Technologies:</div>
                      <div className="flex flex-row flex-wrap gap-2">
                        {Array.from(new Set(techIds.filter((tech) => selectedTechnologies.includes(tech)))).map((tech, i) => (
                          <Pill key={"tech-"+tech} type="technology">{technologyOptions.find(opt => opt.id === tech)?.name || tech}</Pill>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Platforms */}
                  {selectedPlatforms.length > 0 && Array.isArray(cro['Platforms (new)'] || cro['Platforms']) && (cro['Platforms (new)'] || cro['Platforms']).filter((platform: any) => selectedPlatforms.includes(platform)).length > 0 && (
                    <div>
                      <div className="text-xs text-neutral-500 font-semibold mb-1">Platforms:</div>
                      <div className="flex flex-row flex-wrap gap-2">
                        {Array.from(new Set((cro['Platforms (new)'] || cro['Platforms']).filter((platform: any) => selectedPlatforms.includes(platform)))).map((platform: any, i) => (
                          <Pill key={"plat-"+platform} type="platform">{platformOptions.find(opt => opt.id === platform)?.name || platform}</Pill>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Instruments */}
                  {selectedInstruments.length > 0 && Array.isArray(cro['Instruments']) && cro['Instruments'].filter((inst) => selectedInstruments.includes(inst)).length > 0 && (
                    <div>
                      <div className="text-xs text-neutral-500 font-semibold mb-1">Instruments:</div>
                      <div className="flex flex-row flex-wrap gap-2">
                        {Array.from(new Set(cro['Instruments'].filter((inst) => selectedInstruments.includes(inst)))).map((inst, i) => (
                          <Pill key={"inst-"+inst} type="instrument">{instrumentOptions.find(opt => opt.id === inst)?.name || inst}</Pill>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Software */}
                  {selectedSoftware.length > 0 && Array.isArray(cro['Software']) && cro['Software'].filter((soft) => selectedSoftware.includes(soft)).length > 0 && (
                    <div>
                      <div className="text-xs text-neutral-500 font-semibold mb-1">Software:</div>
                      <div className="flex flex-row flex-wrap gap-2">
                        {Array.from(new Set(cro['Software'].filter((soft) => selectedSoftware.includes(soft)))).map((soft, i) => (
                          <Pill key={"soft-"+soft} type="software">{softwareOptions.find(opt => opt.id === soft)?.name || soft}</Pill>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Compliance */}
                  {selectedCompliances.length > 0 && Array.isArray(compliance) && compliance.filter((comp) => selectedCompliances.includes(comp)).length > 0 && (
                    <div>
                      <div className="text-xs text-neutral-500 font-semibold mb-1">Compliance:</div>
                      <div className="flex flex-row flex-wrap gap-2">
                        {Array.from(new Set(compliance.filter((comp) => selectedCompliances.includes(comp)))).map((comp, i) => (
                          <Pill key={"comp-"+comp} type="compliance">{comp}</Pill>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Add more pills as needed */}
                </div>
              </div>
            );
            return slug ? (
              <Link key={cro.id || cro.slug || cro.name || idx} href={`/cros/${slug}`} className="block group focus:outline-none" role="link" tabIndex={0} aria-label={`View details for ${name}`}>{CardContent}</Link>
            ) : (
              <div key={cro.id || cro.name || idx}>{CardContent}</div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {filteredCROs.map((cro: any, idx: number) => {
            const { logoSrc, name, description, location, slug } = extractFields(cro);
            const serviceTypes = cro['Service Types'] || [];
            const techIds = cro['Technologies (new)'] || cro['Technologies'] || [];
            const compliance = cro['Regulatory Compliance'] || [];
            const CardContent = (
              <div className="flex flex-col p-6 rounded-xl shadow-lg bg-white bg-card hover:shadow-2xl hover:-translate-y-1 transition-all w-full cursor-pointer focus:outline-none">
                <div className="flex items-center gap-4 w-full mb-2">
                  <div className="flex-shrink-0 w-28 h-16 flex items-center justify-center bg-white rounded-lg overflow-hidden">
                    {logoSrc ? (
                      <img src={logoSrc} alt={name} className="w-full h-full object-contain" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-neutral-100">
                        <svg width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="64" height="64" rx="12" fill="#E5E7EB"/>
                          <path d="M20 44V40C20 37.7909 21.7909 36 24 36H40C42.2091 36 44 37.7909 44 40V44" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="32" cy="28" r="6" stroke="#A3A3A3" strokeWidth="2"/>
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <span className="font-display font-bold text-xl text-primary-700 truncate">{name}</span>
                    {location && <span className="font-sans italic text-secondary-600 text-sm mt-1 truncate">{location}</span>}
                  </div>
                </div>
                {description && <div className="font-sans text-base text-neutral-700 mt-1 mb-2 break-words line-clamp-2 md:line-clamp-3">{description}</div>}
                <div className="flex flex-col gap-2 mt-1">
                  {/* Service Types */}
                  {selectedServiceTypes.length > 0 && Array.isArray(serviceTypes) && serviceTypes.filter((type) => selectedServiceTypes.includes(type)).length > 0 && (
                    <div>
                      <div className="text-xs text-neutral-500 font-semibold mb-1">Service Types:</div>
                      <div className="flex flex-row flex-wrap gap-2">
                        {Array.from(new Set(serviceTypes.filter((type) => selectedServiceTypes.includes(type)))).map((type, i) => (
                          <Pill key={"svc-"+type} type="serviceType">{type}</Pill>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Technologies */}
                  {selectedTechnologies.length > 0 && Array.isArray(techIds) && techIds.filter((tech) => selectedTechnologies.includes(tech)).length > 0 && (
                    <div>
                      <div className="text-xs text-neutral-500 font-semibold mb-1">Technologies:</div>
                      <div className="flex flex-row flex-wrap gap-2">
                        {Array.from(new Set(techIds.filter((tech) => selectedTechnologies.includes(tech)))).map((tech, i) => (
                          <Pill key={"tech-"+tech} type="technology">{technologyOptions.find(opt => opt.id === tech)?.name || tech}</Pill>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Platforms */}
                  {selectedPlatforms.length > 0 && Array.isArray(cro['Platforms (new)'] || cro['Platforms']) && (cro['Platforms (new)'] || cro['Platforms']).filter((platform: any) => selectedPlatforms.includes(platform)).length > 0 && (
                    <div>
                      <div className="text-xs text-neutral-500 font-semibold mb-1">Platforms:</div>
                      <div className="flex flex-row flex-wrap gap-2">
                        {Array.from(new Set((cro['Platforms (new)'] || cro['Platforms']).filter((platform: any) => selectedPlatforms.includes(platform)))).map((platform: any, i) => (
                          <Pill key={"plat-"+platform} type="platform">{platformOptions.find(opt => opt.id === platform)?.name || platform}</Pill>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Instruments */}
                  {selectedInstruments.length > 0 && Array.isArray(cro['Instruments']) && cro['Instruments'].filter((inst) => selectedInstruments.includes(inst)).length > 0 && (
                    <div>
                      <div className="text-xs text-neutral-500 font-semibold mb-1">Instruments:</div>
                      <div className="flex flex-row flex-wrap gap-2">
                        {Array.from(new Set(cro['Instruments'].filter((inst) => selectedInstruments.includes(inst)))).map((inst, i) => (
                          <Pill key={"inst-"+inst} type="instrument">{instrumentOptions.find(opt => opt.id === inst)?.name || inst}</Pill>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Software */}
                  {selectedSoftware.length > 0 && Array.isArray(cro['Software']) && cro['Software'].filter((soft) => selectedSoftware.includes(soft)).length > 0 && (
                    <div>
                      <div className="text-xs text-neutral-500 font-semibold mb-1">Software:</div>
                      <div className="flex flex-row flex-wrap gap-2">
                        {Array.from(new Set(cro['Software'].filter((soft) => selectedSoftware.includes(soft)))).map((soft, i) => (
                          <Pill key={"soft-"+soft} type="software">{softwareOptions.find(opt => opt.id === soft)?.name || soft}</Pill>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Compliance */}
                  {selectedCompliances.length > 0 && Array.isArray(compliance) && compliance.filter((comp) => selectedCompliances.includes(comp)).length > 0 && (
                    <div>
                      <div className="text-xs text-neutral-500 font-semibold mb-1">Compliance:</div>
                      <div className="flex flex-row flex-wrap gap-2">
                        {Array.from(new Set(compliance.filter((comp) => selectedCompliances.includes(comp)))).map((comp, i) => (
                          <Pill key={"comp-"+comp} type="compliance">{comp}</Pill>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Add more pills as needed */}
                </div>
              </div>
            );
            return slug ? (
              <Link key={cro.id || cro.slug || cro.name || idx} href={`/cros/${slug}`} className="block group focus:outline-none" role="link" tabIndex={0} aria-label={`View details for ${name}`}>{CardContent}</Link>
            ) : (
              <div key={cro.id || cro.name || idx}>{CardContent}</div>
            );
          })}
        </div>
      )}
    </LazySection>
  );
} 